import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import api from "../../services/api.js";

const CustomerTransactionView = ({ role, cust_id, token }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  // Helper: Format Rupiah
  const formatRupiah = (num) => {
    const parsed = parseFloat(num);
    if (isNaN(parsed) || parsed === 0) return "-";
    return parsed.toLocaleString("id-ID");
  };

  // Helper: Format Date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Function Fetch Data
  const fetchTransactions = useCallback(async () => {
    if (!cust_id || !token) return;

    setLoading(true);
    try {
      // Jika 'api' adalah instance Axios
      const response = await api.get(`/transaction/${cust_id}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      });

      // Axios menaruh hasil response body di property .data
      const resData = response.data;

      // Cek status 0 sesuai spek response lo
      if (resData && resData.status === 0) {
        setTransactions(resData.data || []);
      } else {
        console.error("Gagal mengambil data:", resData?.message);
        setTransactions([]);
      }
    } catch (err) {
      // Untuk Axios, detail error ada di err.response
      console.error(
        "Error fetching transactions:",
        err.response?.data || err.message
      );
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [cust_id, token]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const handleDeleteTransaction = async (transactionId) => {
    if (!transactionId) return;

    const confirmDelete = window.confirm(
      "Yakin ingin menghapus transaksi ini?\nInvoice terkait akan dikembalikan ke status Unpaid."
    );

    if (!confirmDelete) return;

    try {
      setDeletingId(transactionId);

      await api.delete(`/delete-transaction/${transactionId}`, {
        headers: {
          Authorization: token,
        },
      });

      alert("Transaksi berhasil dihapus");

      // refresh data
      fetchTransactions();
    } catch (err) {
      console.error("Delete transaction failed:", err);
      alert(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Gagal menghapus transaksi"
      );
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      {/* HEADER SECTION */}
      <div className="flex items-center gap-2 mb-4">
        <div className="w-1.5 h-5 bg-green-600 rounded-full"></div>
        <h2 className="text-xl font-bold dark:text-white">
          Transaction History
        </h2>
      </div>

      {/* CONTENT SECTION */}
      {loading ? (
        <p className="text-gray-500 dark:text-gray-400">
          Loading transactions...
        </p>
      ) : !transactions.length ? (
        <div className="p-8 border-2 border-dashed border-gray-200 dark:border-gray-800 rounded-lg text-center">
          <p className="text-red-500 dark:text-red-300">
            No transactions found for this customer.
          </p>
        </div>
      ) : (
        <motion.div
          className="w-full overflow-x-auto"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-900 text-gray-700 dark:text-gray-300">
                  <th className="p-3 border-b dark:border-gray-700 text-sm">
                    Date
                  </th>
                  <th className="p-3 border-b dark:border-gray-700 text-sm">
                    Description
                  </th>
                  <th className="p-3 border-b dark:border-gray-700 text-sm">
                    Gateway
                  </th>
                  <th className="p-3 border-b dark:border-gray-700 text-sm">
                    Payment Method
                  </th>
                  <th className="p-3 border-b dark:border-gray-700 text-sm">
                    Amount In
                  </th>
                  <th className="p-3 border-b dark:border-gray-700 text-sm">
                    Fees
                  </th>
                  {role === "billing" && (
                    <th className="p-3 border-b dark:border-gray-700 text-sm">
                      Delete
                    </th>
                  )}
                </tr>
              </thead>

              <tbody>
                {transactions.map((trx) => (
                  <tr
                    key={trx.transaction_id} // Pakai transaction_id dari response
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                  >
                    <td className="p-3 border-b dark:border-gray-700 text-sm whitespace-nowrap">
                      {formatDate(trx.transaction_date)}
                    </td>
                    <td className="p-3 border-b dark:border-gray-700">
                      <div className="text-sm font-medium dark:text-gray-200">
                        {trx.description || "No Description"}
                      </div>
                      <div className="text-[10px] text-gray-400">
                        ID: {trx.transid}
                      </div>
                    </td>
                    <td className="p-3 border-b dark:border-gray-700 text-sm text-gray-500">
                      <span className="px-2 py-1 bg-gray-200 dark:bg-gray-800 rounded text-[11px]">
                        {trx.gateway || "MANUAL"}
                      </span>
                    </td>
                    <td className="p-3 border-b dark:border-gray-700 text-sm text-gray-500">
                      <span className="px-2 py-1 bg-gray-200 dark:bg-gray-800 rounded text-[11px]">
                        {trx.paymentmethod || "Unknown"}
                      </span>
                    </td>
                    <td className="p-3 border-b dark:border-gray-700 text-sm font-bold text-green-600">
                      {parseFloat(trx.amountin) > 0
                        ? `+ ${formatRupiah(trx.amountin)}`
                        : "-"}
                    </td>
                    <td className="p-3 border-b dark:border-gray-700 text-sm text-gray-500">
                      {parseFloat(trx.fees) > 0 ? formatRupiah(trx.fees) : "-"}
                    </td>
                    <td className="p-3 border-b dark:border-gray-700 text-sm text-gray-500">
                      {role === "billing" && (
                        <button
                          onClick={() =>
                            handleDeleteTransaction(trx.transaction_id)
                          }
                          disabled={deletingId === trx.transaction_id}
                          className={`px-3 py-1 text-xs rounded transition
    ${
      deletingId === trx.transaction_id
        ? "bg-gray-400 cursor-not-allowed"
        : "bg-red-600 hover:bg-red-700 text-white"
    }
  `}
                        >
                          {deletingId === trx.transaction_id
                            ? "Deleting..."
                            : "Delete"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}
    </>
  );
};

export default CustomerTransactionView;
